import * as React from "react";

import { asString } from "common/components/component-helpers";
import { LOADING } from "common/components/types";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { styled } from "../styled-components";
import { massageCloudURL } from "common/components/portable-renderers";
import { onVisibilityChange, removeOnVisibilityChangeHandler, isDocumentHidden } from "../../lib/browser-hacks";
import { standalone } from "../lib/device";
import { browserMightBeOniOS } from "lib/browser-detect";
import { getFeatureSetting } from "common/feature-settings";
import { useForce } from "hooks/use-force";
import { useForceValueProducers } from "hooks/use-value-producers";

const Wrapper = styled.div`
    position: relative;
    text-align: center;
    padding: ${p => p.theme.componentSmallInnerVerticalPadding}px ${p => p.theme.pageMarginMedium}px;
`;

const AudioWrapper = styled.audio`
    width: 100%;
`;

class Audio extends React.PureComponent<{ src: string }> {
    private audioRef = React.createRef<HTMLAudioElement>();

    private muteAudioIfOniOSHomeScreen = () => {
        if (
            !browserMightBeOniOS ||
            !standalone ||
            !isDocumentHidden() ||
            getFeatureSetting("attemptiOSPWABackgroundAudio")
        ) {
            return;
        }

        const { current } = this.audioRef;
        if (current === null) return;

        current.pause();
    };

    public componentDidMount() {
        onVisibilityChange(this.muteAudioIfOniOSHomeScreen);
    }

    public componentWillUnmount() {
        removeOnVisibilityChangeHandler(this.muteAudioIfOniOSHomeScreen);
    }

    public render() {
        return <AudioWrapper controls={true} src={this.props.src} ref={this.audioRef} />;
    }
}

export const AudioPlayerMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [[audio]] = useForceValueProducers(getValueProducers, force, ctx);
    if (audio === LOADING) return env.renderProgress();
    if (audio === undefined) return null;

    const src = massageCloudURL(asString(audio), true);

    return (
        <Wrapper data-test="app-audio-wrapper">
            <Audio src={src} />
        </Wrapper>
    );
};
